<template>
  <div>
    <DebugInspector :data="hotel" label="Hotel">
      <div
        class="results-list__wrapper"
        :class="{
          'results-list__wrapper--collapse': collapseItem || collapseOverBudget,
          'mt-5': showBusinessSelectBadge && !collapseOverBudget
        }"
      >
        <div
          class="results-list__wrapper--thumbnails"
          v-if="hotel.thumbnails && !collapseItem && !collapseOverBudget"
          @click="gotoHotelDetail()"
          style="cursor: pointer"
        >
          <div
            :style="{
              'background-image': 'url(' + hotel.content.hero.link + ')'
            }"
          ></div>
        </div>

        <div class="results-list__wrapper--content">
          <a
            class="results-list__item__name u-text-color-brand"
            :href="hotel.link"
            v-if="!collapseItem && !collapseOverBudget"
          >
            <span
              class="u-text-color-tertiary u-display--flex u-weight--bold is-align-items-center"
              v-if="hotel.isRecommended"
            >
              <img
                src="@/assets/images/icons/price-tag.svg"
                class="mr-1"
                width="20"
              />
              {{ $t("recommended.hotel") }}
            </span>
            {{ getHotelNameWithCity(hotel) }}
            <span
              v-if="hotel.hasGreenKey"
              style="margin-top: 3px; margin-left: 2px"
            >
              <img
                width="20"
                height="50"
                src="@/assets/images/greenkey-nobg.png"
              />
            </span>
          </a>
          <span class="results-list__item__name u-text-color-brand" v-else>
            {{ getHotelNameWithCity(hotel) }}
          </span>
          <div
            style="max-width: 290px"
            v-if="!collapseItem && !collapseOverBudget"
          >
            <AmenitiesLabel :amenities="hotel.amenities" />
          </div>
          <LocationLabel
            :distance="hotel.distance"
            :hotel-city="hotel.city"
            :city="location.name"
            @click="showHotelMarker()"
          />
          <ReviewsLabel
            :score="parseFloat(hotel.reviewScore.toPrecision(2))"
            :amount="hotel.reviewAmount"
            v-if="!collapseItem && !collapseOverBudget"
          />
        </div>
        <div
          class="results-list__wrapper--price is-flex is-justify-content-center is-align-items-center"
        >
          <template v-if="!collapseItem && !collapseOverBudget">
            <PriceLabel
              v-if="done"
              :hotel="hotel"
              :hotel-link="hotel.link"
              :reservation-link="hotel.reservationLink"
              :nights="nights"
              @goto-detail="gotoHotelDetail"
              @goto-reservation="gotoHotelReservation"
            />
          </template>
          <template v-else-if="collapseOverBudget">
            <p class="has-text-black is-size-7 has-background-warning p-3">
              {{ $t("warningOverBudget", { budget }) }}
            </p>
          </template>
          <template v-else>
            <span class="u-text-color-red">{{ $t("noAvailability") }}</span>
          </template>
        </div>
      </div>
    </DebugInspector>
    <div
      class="results-list__wrapper--business-select-badge is-flex is-justify-content-center is-align-items-center"
      v-if="showBusinessSelectBadge && !collapseOverBudget"
    >
      <img
        src="@/assets/images/beeldmerk-l4b-black.svg"
        class="mr-2"
        width="25"
      />
      <h6 class="title is-6 mb-0">Business select</h6>
      <img
        src="@/assets/images/icons/icon-info.svg"
        class="ml-2 is-block js-tooltip"
        width="15"
        data-tooltip-edge-padding='{"right":40}'
        :data-tooltip-content="$t('businessSelectTooltip')"
      />
    </div>
  </div>
</template>

<script>
import AmenitiesLabel from "./AmenitiesLabel.vue";
import LocationLabel from "./LocationLabel.vue";
import ReviewsLabel from "./ReviewsLabel.vue";
import PriceLabel from "./PriceLabel.vue";
import HotelHelper from "@/helpers/hotel.helper.js";
import CommonHelper from "@/helpers/common.helper.js";
import DebugInspector from "@/components/shared/DebugInspector.vue";

export default {
  components: {
    AmenitiesLabel,
    LocationLabel,
    ReviewsLabel,
    PriceLabel,
    DebugInspector
  },
  data() {
    return {
      cdnUrl: this.CDN_URL,
      lowestOffer: null,
      budget: null,
      companyInfo: null
    };
  },
  async created() {
    const companyInfo = await CommonHelper.getCompanyInfo();
    this.companyInfo = companyInfo;
    this.budget = companyInfo.budget;
  },
  methods: {
    gotoHotelDetail() {
      this.$emit("gotoDetail", this.hotel.hotelId);
    },
    gotoHotelReservation() {
      this.$emit("gotoReservation", this.hotel.hotelId);
    },
    showHotelMarker() {
      this.$emit("showMarker", this.hotel.hotelId);
    }
  },
  computed: {
    collapseItem() {
      return this.lowestOffer == null && this.collapseOfferless;
    },
    isOverBudget() {
      if (!this.budget || this.budget == null) {
        return false;
      }
      const pricePerNight = HotelHelper.getBarePricePerNight(
        this.lowestOffer,
        this.nights
      );
      return pricePerNight > this.budget;
    },
    canEditExpenses() {
      return HotelHelper.canEditExpenses(this.companyInfo);
    },
    canBookOnBehalfOfOthers() {
      return HotelHelper.canBookOnBehalfOfOthers(this.companyInfo);
    },
    collapseOverBudget() {
      return this.isOverBudget && !this.canBookOnBehalfOfOthers;
    }
  },
  watch: {
    "hotel.suppliers": {
      handler() {
        this.lowestOffer = HotelHelper.getComparisonDisplayOffer(this.hotel);
      },
      immediate: true
    }
  },
  name: "ResultItem",
  emits: ["gotoDetail", "gotoReservation", "showMarker"],
  props: {
    hotel: {},
    location: {},
    locationPath: String,
    collapseOfferless: Boolean,
    done: Boolean,
    feedbackId: String,
    checkin: String,
    adults: [String, Number],
    rooms: [String, Number],
    nights: [String, Number],
    showBusinessSelectBadge: Boolean
  }
};
</script>
